<template>
  <div class="ruta">
    <div
      v-if="cargando"
      class="estado"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <div
      v-else-if="noExiste"
      class="estado"
    >
      <v-icon
        size="52"
        color="grey-lighten-1"
      >
        mdi-map-marker-question-outline
      </v-icon>
      <strong>Esta ruta no está disponible</strong>
      <span>Puede que se haya despublicado o que el enlace esté mal escrito.</span>
      <v-btn
        color="primary"
        variant="tonal"
        @click="$router.push({ name: 'RutasView' })"
      >
        Ver todas las rutas
      </v-btn>
    </div>

    <template v-else>
      <!-- PORTADA. Va arriba y a todo el ancho, con el titulo DEBAJO y no
           encima: un degradado oscuro sobre la foto para poder leer el texto
           arruina justo lo que se quiere enseñar. -->
      <div
        v-if="hayPortada"
        class="marco-portada"
      >
        <div
          v-if="cargandoPortada"
          class="capa-carga"
        >
          <v-progress-circular
            indeterminate
            color="white"
            size="30"
            width="3"
          />
        </div>
        <v-img
          v-else
          :src="portada"
          :alt="`Foto de ${datos.name}`"
          cover
          eager
          height="100%"
        />
      </div>

      <!-- Las demas imagenes propias de la ruta, en la segunda tanda -->
      <div
        v-if="galeria.length > 0"
        class="tira-portada"
      >
        <div
          v-for="(foto, indice) in galeria"
          :key="indice"
          class="mini-portada"
        >
          <v-img
            :src="foto"
            :alt="`Foto de ${datos.name}`"
            cover
            eager
            :aspect-ratio="1"
          />
        </div>
      </div>

      <div class="cabecera">
        <button
          type="button"
          class="volver"
          @click="$router.push({ name: 'RutasView' })"
        >
          <v-icon size="16">mdi-arrow-left</v-icon>
          Rutas
        </button>

        <h1 class="titulo">
          {{ datos.name }}
        </h1>

        <div class="meta">
          <span
            v-if="ubicacion"
            class="dato"
          >
            <v-icon size="14">mdi-map-marker-outline</v-icon>
            {{ ubicacion }}
          </span>
          <span class="dato">
            <v-icon size="14">mdi-map-marker-path</v-icon>
            {{ datos.stops.length }} {{ datos.stops.length === 1 ? 'parada' : 'paradas' }}
          </span>
          <span
            v-if="duracionTotal"
            class="dato"
          >
            <v-icon size="14">mdi-clock-outline</v-icon>
            {{ duracionTotal }}
          </span>
        </div>

        <p
          v-if="datos.description"
          class="descripcion"
        >
          {{ datos.description }}
        </p>

        <v-btn
          variant="tonal"
          color="primary"
          size="small"
          class="mt-3"
          @click="compartir"
        >
          <v-icon start size="17">
            mdi-share-variant
          </v-icon>
          Compartir
        </v-btn>
      </div>

      <div
        v-if="datos.stops.length === 0"
        class="estado"
      >
        <v-icon
          size="48"
          color="grey-lighten-1"
        >
          mdi-map-marker-off-outline
        </v-icon>
        <strong>Las paradas de esta ruta no están disponibles por ahora</strong>
        <span>Vuelve pronto: el recorrido sigue aquí, solo que sus sitios no se están mostrando.</span>
      </div>

      <!-- La linea del recorrido: el orden es la informacion, asi que se dibuja
           como una secuencia y no como una lista suelta de tarjetas. -->
      <ol
        v-else
        class="recorrido"
      >
        <li
          v-for="parada in datos.stops"
          :key="parada.idStop"
          class="parada"
        >
          <div class="marcador">
            <span class="numero">{{ parada.order }}</span>
          </div>

          <TarjetaParada
            :parada="parada"
            @abrir="abrirParada"
          />
        </li>
      </ol>
    </template>

    <v-snackbar
      v-model="aviso"
      :timeout="3000"
      color="success"
      location="top"
    >
      {{ mensajeAviso }}
    </v-snackbar>
  </div>
</template>

<script>
import apisRutas from '@/apis/apisRutas'
import apisImagenes from '@/apis/apisImagenes'
import TarjetaParada from '@/components/Rutas/TarjetaParada.vue'
import { ponerTitulo } from '@/router'

/** Cuantas fotos extra de la ruta se muestran bajo la portada. */
const MAX_GALERIA = 4

/**
 * Una ruta como la ve un visitante.
 *
 * Se pide por SLUG y no por id: es la URL que se comparte y la que posiciona en
 * buscadores, que para un directorio es medio negocio.
 *
 * Las paradas llegan ya filtradas y renumeradas desde 1 por el backend, asi que
 * aqui nunca hay que decidir si una empresa se muestra o no.
 */
export default {
  name: 'ViewRutaView',
  components: { TarjetaParada },
  data() {
    return {
      datos: null,
      cargando: true,
      noExiste: false,
      imagenPortada: null,
      cargandoPortada: true,
      galeria: [],
      aviso: false,
      mensajeAviso: ''
    }
  },
  computed: {
    /**
     * Hay portada si la ruta tiene imagen propia o si alguna parada la puede
     * prestar. Sin ninguna de las dos, el marco no se pinta: un rectangulo
     * gris a todo el ancho es peor que empezar por el titulo.
     */
    hayPortada() {
      if (!this.datos) {
        return false
      }
      return this.datos.ownImages === true || this.datos.stops.length > 0
    },
    portada() {
      return this.imagenPortada
    },
    ubicacion() {
      if (!this.datos) {
        return ''
      }
      return [this.datos.city, this.datos.department].filter(Boolean).join(', ')
    },
    duracionTotal() {
      if (!this.datos) {
        return null
      }

      const total = this.datos.stops.reduce(
        (suma, parada) => suma + (parada.estimatedMinutes || 0), 0
      )

      if (total === 0) {
        return null
      }
      if (total < 60) {
        return `${total} min`
      }

      const horas = Math.floor(total / 60)
      const resto = total % 60

      return resto === 0 ? `${horas} h` : `${horas} h ${resto} min`
    }
  },
  watch: {
    // Al pasar de una ruta a otra sin salir de la vista, created ya no corre
    '$route.params.slug'() {
      this.cargar()
    }
  },
  created() {
    this.cargar()
  },
  methods: {
    cargar() {
      this.cargando = true
      this.noExiste = false

      this.imagenPortada = null
      this.galeria = []

      apisRutas.porSlug(this.$route.params.slug)
        .then(ruta => {
          this.datos = ruta
          ponerTitulo(ruta.name)
          this.contarVista(ruta.idRoute)
          this.cargarPortada()
        })
        .catch(() => {
          // Una ruta despublicada responde 404 igual que una que no existe, asi
          // que para el visitante son el mismo caso y se cuentan igual
          this.noExiste = true
          ponerTitulo('Ruta no encontrada')
        })
        .finally(() => {
          this.cargando = false
        })
    },
    /**
     * Suma la visita. Va despues de tener la ruta cargada, no antes: solo
     * cuenta si el visitante de verdad llego a verla, y no un 404.
     *
     * El fallo se ignora en silencio. Si el contador no responde, al visitante
     * no le cambia nada y no tiene por que enterarse; es un dato nuestro.
     */
    contarVista(idRuta) {
      if (!idRuta) {
        return
      }

      apisRutas.sumarVista(idRuta).catch(() => {})
    },
    /**
     * Primera tanda: una sola foto, la portada.
     *
     * Si la ruta tiene imagen propia manda esa, que es la que eligio quien
     * armo el recorrido. Si no, se presta la de la primera parada: enseña algo
     * que de verdad esta en el camino.
     */
    cargarPortada() {
      const propia = this.datos.ownImages === true
      const primera = this.datos.stops[0]

      if (!propia && !primera) {
        this.cargandoPortada = false
        return
      }

      this.cargandoPortada = true

      const peticion = propia
        ? apisImagenes.principal('ruta', this.datos.idRoute)
        : apisImagenes.principal(primera.targetType, primera.idTarget)

      peticion
        .catch(() => null)
        .then(imagen => {
          this.imagenPortada = imagen
        })
        .finally(() => {
          this.cargandoPortada = false

          // Segunda tanda: las demas fotos de la ruta. Solo si son suyas; las
          // de las paradas ya se ven en cada tarjeta del recorrido y repetirlas
          // aqui seria enseñar dos veces lo mismo.
          if (propia && this.imagenPortada) {
            this.cargarGaleria()
          }
        })
    },
    cargarGaleria() {
      apisImagenes.resto('ruta', this.datos.idRoute, MAX_GALERIA)
        .then(fotos => {
          this.galeria = fotos || []
        })
        .catch(() => {
          this.galeria = []
        })
    },
    abrirParada(parada) {
      const vistas = {
        empresa: 'ViewCompanyView',
        producto: 'ViewProductView',
        lugar: 'ViewSiteView'
      }

      this.$router.push({
        name: vistas[parada.targetType] || 'ViewSiteView',
        params: { id: parada.idTarget }
      })
    },
    async compartir() {
      const url = window.location.href

      // navigator.share solo existe en moviles y en contexto seguro; en
      // escritorio se cae al portapapeles
      if (navigator.share) {
        try {
          await navigator.share({ title: this.datos.name, url })
          return
        } catch (e) {
          // Cancelar el menu de compartir no es un error que haya que contar
          return
        }
      }

      try {
        await navigator.clipboard.writeText(url)
        this.mensajeAviso = 'Enlace copiado'
        this.aviso = true
      } catch (e) {
        this.mensajeAviso = 'No se pudo copiar el enlace'
        this.aviso = true
      }
    }
  }
}
</script>

<style scoped>
.ruta {
  max-width: 760px;
  margin: 0 auto;
  padding: 18px 14px 48px;
}

/* PORTADA */
.marco-portada {
  position: relative;
  aspect-ratio: 16 / 9;
  /* Sin esto el aspect-ratio se lo come el min-height: auto */
  min-height: 0;
  margin-bottom: 10px;
  border-radius: 14px;
  overflow: hidden;
  background: #0d3f75;
}
/* Mas panoramica en pantalla grande: a 16/9 y 760px de ancho la foto se come
   la pantalla entera y el titulo queda fuera del primer golpe de vista */
@media (min-width: 700px) {
  .marco-portada {
    aspect-ratio: 21 / 9;
  }
}
.capa-carga {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(to bottom right, #42a5f5 0%, #1976d2 45%, #0d3f75 100%);
}

.tira-portada {
  display: flex;
  gap: 6px;
  margin-bottom: 14px;
  overflow-x: auto;
  padding-bottom: 2px;
}
.mini-portada {
  flex: 0 0 auto;
  width: 84px;
  height: 84px;
  border-radius: 9px;
  overflow: hidden;
  background: #f2f5f9;
}

.volver {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 10px;
  color: #1976d2;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  background: none;
  border: 0;
  padding: 0;
}

.cabecera {
  margin-bottom: 26px;
}
.titulo {
  margin: 0;
  font-size: 1.7rem;
  font-weight: 800;
  line-height: 1.2;
  color: #1a1a1a;
  overflow-wrap: anywhere;
}
.meta {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 10px;
}
.dato {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 9px;
  background: #f2f5f9;
  color: #475467;
  font-size: 0.74rem;
  font-weight: 600;
}
.descripcion {
  margin: 12px 0 0;
  font-size: 0.92rem;
  line-height: 1.55;
  color: #475467;
  white-space: pre-line;
}

/* EL RECORRIDO */
.recorrido {
  list-style: none;
  margin: 0;
  padding: 0;
}
.parada {
  display: flex;
  gap: 14px;
  align-items: stretch;
}

/* La linea vertical se dibuja en el marcador y se corta en la ultima parada:
   asi el recorrido se lee como una secuencia y no como tarjetas sueltas. */
.marcador {
  position: relative;
  flex: 0 0 auto;
  width: 30px;
  display: flex;
  justify-content: center;
}
.marcador::before {
  content: '';
  position: absolute;
  top: 30px;
  bottom: -14px;
  width: 2px;
  background: #dbe3ec;
}
.parada:last-child .marcador::before {
  display: none;
}
.numero {
  position: relative;
  z-index: 1;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #1976d2;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.estado {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 64px 12px;
  color: #667085;
  font-size: 0.9rem;
  text-align: center;
}
.estado strong {
  font-size: 1rem;
  color: #1a1a1a;
}
</style>
